import React, { useState } from 'react';
import styled from 'styled-components';
import { PoliceReportsService } from '../../services/police-reports-service';

const Title = styled.h3`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 20px;
    font-family: ${props => props.theme.font.family};
    font-size: ${props => props.theme.font.size.large};
    font-style: ${props => props.theme.font.style};
    font-weight: ${props => props.theme.font.weight.regular};
`;

const Input = styled.input`
    width: 100%;
    padding: 15px;
    resize: none;
    margin: 5px;
    display: inline-block;  
    outline: 0;
    background: #f2f2f2;
    border: 0;
    border-radius: .25rem;
    box-sizing: border-box;
`;

const RegisterFormContainer = styled.form`
    position: relative;
    margin: 0 auto 100px;
    text-align: center;
`;

const Button = styled.button`
    text-transform: uppercase;
    outline: 0;
    background: #3CAB3D;
    width: 100%;
    border: 0;
    padding: 15px;
    color: #FFFFFF;
    font-size: 14px;
    -webkit-transition: all 0.3 ease;
    transition: all 0.3 ease;
    cursor: pointer;
    border: none;
    border-radius: .25rem;
    padding: 15px 32px;  
    margin: .5rem;  
    margin-top: 2rem;
    text-align: center;  
    text-decoration: none;  
    display: inline-block;  
    font-size: 16px;  

  &:hover {
      background: #43A047;
  }
`;

const Message = styled.p`
  margin: 15px 0 0;
  color: #b3b3b3;
    font-size: ${props => props.theme.font.size.small};
    &:a {
      color: #4CAF50;
      text-decoration: none;
    }
`;

const ListItem = styled.li`
    text-align: left;
    padding-bottom: 1rem;
`;

const Checkbox = styled.input`
    width: 20px;
    height: 20px;
    cursor: pointer;
`;

const CheckboxLabel = styled.label`
    font-family: ${props => props.theme.font.family};
    font-size: ${props => props.theme.font.size.regular};
    font-style: ${props => props.theme.font.style};
    font-weight: ${props => props.theme.font.weight.semiBold};
    padding: 10px 20px;
    text-align: left;
    bold: true;
`;

const AgreeBox = styled.div`
    display: flex;
    align-items: center;
    flex-direction: flex-start;
    padding: 1em;
`;

interface RegisterFormProps {
    email: string;
    setEmail: (email: string) => void;
    emailError: string;
    setEmailError: (error: string) => void;
    setFormState: (state: 'login' | 'register' | 'changePassword') => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ email, setEmail, emailError, setEmailError, setFormState }) => {
    const [isAgree, setIsAgree] = useState(false);

    const validateEmail = (email: string) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    }

    const clickRegister = async () => {
        if (!validateEmail(email)) {
            setEmailError("Invalid email format");
            return;
        }

        try {
            await PoliceReportsService.insertUser(email);
            setFormState('login');
        } catch (error) {
            setEmailError("Failed to register user");
        }
    }

    return (
        isAgree ? (
            <RegisterFormContainer>
                <Title>Register for Online Police Reports</Title>
                <ul>
                    <ListItem>After you have accepted the 'Terms and Conditions', fill out the registration form.</ListItem>
                    <ListItem>If you have forgotten your password, <a href="#" onClick={() => setFormState('changePassword')}>click here</a> to have a new one emailed to you.</ListItem>
                </ul>
                {emailError && <Message>{emailError}</Message>}
                <Input
                    type="text"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError("");
                    }}
                    placeholder="Email"
                />
                <Button onClick={clickRegister}>
                    Register
                </Button>
                <Message>
                    <a href="#" onClick={() => setFormState('login')}>Cancel</a>
                </Message>
            </RegisterFormContainer>
        ) : (
            <table cellPadding="3" cellSpacing="0">
                <tbody>
                    <tr>
                        <td>
                            <Title>Terms and Conditions of Use</Title>
                            <p>
                                Police reports are available, to the public, at the public counter of the
                                Police Department Records Section. The Police Records public counter is located
                                at 916 Texas Avenue. You may also get a copy of a police report over the
                                Internet using this site. Please read the following terms of use, then click
                                the 'I Agree' button to continue with the signup process. If you do not agree
                                to the terms of use for this service, you must obtain copies of Police Reports
                                at the Police Records public counter.</p>
                            <div>
                                <p>The City of Lubbock will obtain your e-mail address for the purposes of limiting
                                    Internet traffic on the Police Report Request Internet application. Your email
                                    address will be used solely for this purpose and will not be divulged to third
                                    parties, except as allowed by applicable law.</p>
                                <p>By agreeing to these terms of use, you affirm that you will not attempt to
                                    abuse, alter, or otherwise attempt to circumvent controls set up to limit
                                    access to this site, and that you understand that access to this site is not
                                    guaranteed and may be terminated at any time.</p>
                                <p>Your agreement acknowledges that any abuses resulting from the use or attempted
                                    use of this site may subject you to civil or criminal penalties under the laws
                                    of the State of Texas.</p>
                                <p>
                                    You also agree that the City of Lubbock in no way guarantees that this site
                                    will be available at any given time, and that the City is not responsible for
                                    any inconvenience or losses that may occur due to the unavailability of this
                                    site.</p>
                            </div>
                            <AgreeBox>
                                <Checkbox type="checkbox" onClick={() => setIsAgree(!isAgree)} />
                                <CheckboxLabel>I Agree to the above stated Terms and Conditions</CheckboxLabel>
                            </AgreeBox>
                        </td>
                    </tr>
                </tbody>
            </table>
        )
    );
};

export default RegisterForm;
